import { Flex } from '@chakra-ui/react';
import { Button, Text } from '@kula-ai/components';
import { RefreshCw01, XClose } from '@kula-ai/icons-core';
import { colors } from '@/theme/foundations/colors';
import { useNudgeThrottle } from './useNudgeThrottle';

const NEW_VERSION_MESSAGE = 'A new version of Kula is available.';
const REFRESH_LABEL = 'Refresh';
const DISMISS_LABEL = 'Dismiss';

export interface StaleBuildPromptProps {
  /** A newer build is live and nothing else is going to handle it. */
  isStale: boolean;
  reload: () => void;
  nudgeIntervalMs?: number;
}

/**
 * Advisory only — never reloads on its own. Recruiters keep half-filled forms
 * open for hours, so losing that to a surprise refresh would cost more than the
 * stale bundle does. With the silent upgrade on this is the fallback for a tab
 * that could not be upgraded quietly (held, or the reload did not take).
 *
 * Anchored bottom-right, not bottom-centre: TableBulkActions and the interview
 * toolbar both sit fixed at bottom-centre, and this renders above them.
 */
export const StaleBuildPrompt = ({
  isStale,
  reload,
  nudgeIntervalMs,
}: StaleBuildPromptProps) => {
  const { show, dismiss } = useNudgeThrottle(isStale, nudgeIntervalMs);

  // The live region is always mounted and only its contents change: a region
  // that appears already populated is typically announced by nothing.
  return (
    <Flex
      role="status"
      aria-live="polite"
      display={show ? 'flex' : 'none'}
      position="fixed"
      bottom="24px"
      right="24px"
      zIndex="toast"
      align="center"
      gap="12px"
      py="10px"
      pl="16px"
      pr="10px"
      borderRadius="8px"
      bg="white"
      border="1px solid"
      borderColor="border-color"
      boxShadow="0 8px 24px -8px rgba(0, 0, 0, 0.25)"
    >
      <RefreshCw01 size={16} color={colors['secondary-700']} />
      <Text variant="span" color="body">
        {NEW_VERSION_MESSAGE}
      </Text>
      <Button variant="primary" size="sm" onClick={reload}>
        {REFRESH_LABEL}
      </Button>
      <Button
        variant="quaternary"
        size="icon"
        aria-label={DISMISS_LABEL}
        onClick={dismiss}
      >
        <XClose size={16} />
      </Button>
    </Flex>
  );
};
